import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import PdMark from "../components/PdMark";
import { Loader2, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Encryption progress: one plain sentence and a flat bar. No spinning rings.
const EncryptionAnimation = ({ currentFile, progress }) => (
  <div className="py-6 space-y-4" role="status" aria-live="polite">
    <p className="font-heading text-[20px] font-semibold tracking-[-0.02em] text-ink">Encrypting and uploading</p>
    {currentFile && (
      <p className="text-sm text-ink-55 truncate">Working on {currentFile}</p>
    )}
    <div className="w-full bg-inset rounded-full h-1.5 overflow-hidden">
      <div
        className="bg-brand h-full transition-[width] duration-300 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
    <p className="text-sm text-ink-55">
      Files are sealed with the requester&apos;s key before they are stored. Keep this page open.
    </p>
  </div>
);

// Success state component
const UploadSuccess = ({ filesUploaded }) => (
  <div className="py-2 space-y-5">
    <div>
      <h3 className="font-heading text-[24px] font-semibold tracking-[-0.02em] text-ink">Upload complete</h3>
      <p className="text-ink-70 mt-2">
        {filesUploaded.length} file{filesUploaded.length !== 1 ? 's' : ''} encrypted and stored. The requester has been told.
      </p>
    </div>
    {filesUploaded.length > 0 && (
      <ul className="space-y-1.5 text-[15px] text-ink-70">
        {filesUploaded.map((file, idx) => (
          <li key={idx} className="flex items-center gap-3 min-w-0">
            <span className="w-[7px] h-[7px] rounded-full bg-brand flex-shrink-0" aria-hidden="true" />
            <span className="truncate">{file.filename}</span>
          </li>
        ))}
      </ul>
    )}
    <p className="p-4 rounded-lg bg-brand-wash text-brand-ink text-[15px]">
      Only the person who made the request can open these files. This link has now been used and will not work again.
    </p>
  </div>
);

export function SecureUpload() {
  const { token } = useParams();

  const [linkStatus, setLinkStatus] = useState("loading"); // loading, valid, invalid, used, expired
  const [linkInfo, setLinkInfo] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [error, setError] = useState(null);

  // Verify the upload link on mount
  useEffect(() => {
    const verifyLink = async () => {
      try {
        const response = await fetch(`${API}/api/upload-link/verify/${token}`);
        const data = await response.json();

        if (data.valid) {
          setLinkStatus("valid");
          setLinkInfo(data);
        } else {
          if (data.error?.includes("already been used")) {
            setLinkStatus("used");
          } else if (data.error?.includes("expired")) {
            setLinkStatus("expired");
          } else {
            setLinkStatus("invalid");
          }
          setError(data.error);
        }
      } catch (err) {
        console.error("Failed to verify link:", err);
        setLinkStatus("invalid");
        setError("We could not check this upload link. Try again in a moment.");
      }
    };

    if (token) {
      verifyLink();
    } else {
      setLinkStatus("invalid");
      setError("No upload token provided");
    }
  }, [token]);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  }, []);

  const removeFile = useCallback((index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error("Choose at least one file to upload");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append("files", file);
    });

    // Simulate progress for encryption feedback
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 200);

    try {
      // Show each file being processed
      for (let i = 0; i < selectedFiles.length; i++) {
        setCurrentFile(selectedFiles[i].name);
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const response = await fetch(`${API}/api/upload-link/upload/${token}`, {
        method: "POST",
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();

      if (response.ok && data.success) {
        setUploadComplete(true);
        setUploadedFiles(data.files_uploaded || []);
        toast.success("Files uploaded");
      } else {
        throw new Error(data.detail || data.message || "Upload failed");
      }
    } catch (err) {
      clearInterval(progressInterval);
      console.error("Upload failed:", err);
      setError(err.message);
      toast.error(err.message);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const Brand = () => (
    <div className="flex items-center gap-3">
      <PdMark size={28} className="text-brand" />
      <span className="font-heading text-[18px] font-bold tracking-[-0.02em] text-ink">Precious Data</span>
    </div>
  );

  // Loading state
  if (linkStatus === "loading") {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg bg-surface rounded-xl p-7 sm:p-9 flex items-center gap-3 text-ink-70">
          <Loader2 className="w-5 h-5 text-brand animate-spin" />
          <p>Checking this upload link</p>
        </div>
      </div>
    );
  }

  // Invalid/expired/used states
  if (linkStatus !== "valid") {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg">
          <div className="mb-6"><Brand /></div>
          <div className="bg-surface rounded-xl p-7 sm:p-9 space-y-4">
            <h1 className="font-heading text-[28px] font-bold tracking-[-0.035em] leading-[1.05] text-ink">
              {linkStatus === "used" && "This link has already been used"}
              {linkStatus === "expired" && "This link has expired"}
              {linkStatus === "invalid" && "This link does not work"}
            </h1>
            <p className="text-ink-70">
              {linkStatus === "used" && "Each upload link works once, so nobody can add files after you have finished."}
              {linkStatus === "expired" && "Upload links only stay open for a limited time."}
              {linkStatus === "invalid" && "Check that the whole link was copied from the request email."}
            </p>
            <p className="text-ink-70">
              If you still have documents to send, reply to the original request email and ask for a new link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ground px-4 py-10">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6"><Brand /></div>

        {/* Main panel */}
        <div className="bg-surface rounded-xl p-7 sm:p-9">
          <p className="pd-eyebrow">Secure document upload</p>
          <h1 className="font-heading text-[28px] font-bold tracking-[-0.035em] leading-[1.05] text-ink mt-2">
            Upload documents for {linkInfo?.user_name}
          </h1>
          <p className="text-ink-70 mt-2">
            Responding on behalf of {linkInfo?.organisation_name}
          </p>

          <div className="mt-8">
            {/* Upload Complete State */}
            {uploadComplete ? (
              <UploadSuccess filesUploaded={uploadedFiles} />
            ) : uploading ? (
              /* Uploading/Encrypting State */
              <EncryptionAnimation currentFile={currentFile} progress={uploadProgress} />
            ) : (
              /* File Selection State */
              <div className="space-y-6">
                {/* Security Notice */}
                <div className="p-4 rounded-lg bg-ground text-[15px] text-ink-70 space-y-1">
                  <p className="font-semibold text-ink">How this works</p>
                  <ul className="space-y-1 list-disc pl-5">
                    <li>Files are encrypted before they are stored. Only the requester can open them.</li>
                    <li>This link works once. After you upload, it stops working.</li>
                    <li>
                      The link closes on{' '}
                      <span className="pd-mono text-ink">{new Date(linkInfo?.expires_at).toLocaleString()}</span>
                    </li>
                  </ul>
                </div>

                {/* File Drop Zone */}
                <div
                  className="border-2 border-dashed border-rule rounded-lg p-8 text-center hover:border-brand hover:bg-ground transition-colors duration-state cursor-pointer"
                  onClick={() => document.getElementById('file-input').click()}
                  data-testid="file-drop-zone"
                >
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="file-input"
                  />
                  <p className="text-ink font-semibold">Choose files</p>
                  <p className="text-sm text-ink-55 mt-1">or drag and drop them here</p>
                  <p className="text-sm text-ink-55 mt-2">
                    PDF, Word, Excel, images and other common formats
                  </p>
                </div>

                {/* Selected Files List */}
                {selectedFiles.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-[15px] font-semibold text-ink">
                      {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} ready
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selectedFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-ground rounded-lg gap-3"
                        >
                          <div className="min-w-0">
                            <p className="text-[15px] text-ink truncate">{file.name}</p>
                            <p className="pd-mono text-ink-55">{formatFileSize(file.size)}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="flex-shrink-0"
                            aria-label={`Remove ${file.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(idx);
                            }}
                            data-testid={`remove-file-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <p className="p-4 rounded-lg bg-breach-wash text-breach-ink text-[15px]">{error}</p>
                )}

                {/* Upload Button */}
                <Button
                  className="w-full"
                  size="lg"
                  disabled={selectedFiles.length === 0 || uploading}
                  onClick={handleUpload}
                  data-testid="upload-button"
                >
                  Upload {selectedFiles.length > 0 ? `${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''}` : 'files'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-ink-55 mt-6">
          Precious Data helps care leavers get and read their own records.
        </p>
      </div>
    </div>
  );
}

export default SecureUpload;
